import Content, { ContentNS } from "content/content";
import PrimaryButton from "features/shared/components/buttons/PrimaryButton.styled";
import SelectFileButton from "features/shared/components/select-file-button/SelectFileButton";
import useScrollToPageTitle from "features/document-page/hooks/useScrollToPageTitle";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import usePaxSession from "features/shared/hooks/usePaxSession";
import { FC, useEffect, useRef, useState } from "react";
import { DocProcessingStatus } from "models/doc-processing-status";
import { DocumentRequirement } from "models/checklist/checklist-item/checklist-item";
import { useAppStore } from "store/store";

import ManualPassportInput from "./components/manual-passport-input/ManualPassportInput";
import PassportPreview from "./components/passport-preview/PassportPreview";
import useUpdatePassportFromChecklist from "./hooks/useUpdatePassportFromChecklist";
import AutomatedTools from "./styles/AutomatedTools.styled";
import StyledPassportPage from "./styles/StyledPassportPage.styled";
import ToolsButtons from "./styles/ToolsButtons.styled";
import { useSearchParams } from "react-router-dom";
import useItemLevelNavigationState, {
  ItemNavigationState,
} from "features/shared/hooks/navigation/useItemLevelNavigationState";
import { AllowedDocumentType } from "models/documents/allowed-document";
import useUploadSingleSideDocument from "features/shared/hooks/useUploadSingleSideDocument";
import { MicroHeader } from "features/shared/styles/typography/MicroHeader.styled";

const PassportPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [onFileSelected] = useUploadSingleSideDocument(false);

  const paxSession = usePaxSession();

  const passportFormCardRef = useRef<HTMLDivElement>(null);

  const processingError = useAppStore((state) => state.processingError);

  const navigate = useNavigateWithQuery();

  useUpdatePassportFromChecklist(
    paxSession?.paxSessionId,
    paxSession?.pax,
    paxSession?.checklist
  );

  const hasSignalsMismatch = useAppStore((state) => state.hasSignalsMismatch);
  const hasBookingMismatch = useAppStore(
    (state) => state.hasManualBookingMismatch
  );

  const setProcessingStatus = useAppStore((state) => state.setProcessingStatus);
  const setProcessingMayTakeLong = useAppStore(
    (state) => state.setProcessingMayTakeLong
  );

  const setHasManualBookingMismatch = useAppStore(
    (state) => state.setHasManualBookingMismatch
  );
  const docProcessingError = useAppStore((state) => state.processingError);
  const [isFormShown, showForm] = useState(false);

  // skipped while the manual form is up: the page scrolls to that instead
  useScrollToPageTitle(
    !isFormShown && !hasSignalsMismatch && !hasBookingMismatch
  );

  const textPassport = (s: string) =>
    Content.shared.t(s, { ns: ContentNS.passport });

  useEffect(() => {
    if (isFormShown || hasSignalsMismatch || hasBookingMismatch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      showForm(true);

      if (passportFormCardRef.current && !processingError) {
        passportFormCardRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [
    docProcessingError,
    hasBookingMismatch,
    hasSignalsMismatch,
    isFormShown,
    processingError,
  ]);

  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  // Allowed documents for the current item (or for TRAVEL_DOC when navigating
  // by item type). Used to decide which provision methods the API permits.
  const allowedDocuments =
    itemLevelNavigationState instanceof ItemNavigationState &&
    itemLevelNavigationState.item instanceof DocumentRequirement
      ? itemLevelNavigationState.item.allowedDocumentsSet.allowedDocuments
      : paxSession?.checklist.allowedTravelDocumentsSet.allowedDocuments ?? [];

  // Only offer manual entry when the API lists MANUAL among the allowed methods.
  const isManualInputAllowed = allowedDocuments.some((doc) =>
    doc.isManualPossible()
  );

  // we should enforce Passport type for better results
  useEffect(() => {
    if (
      itemLevelNavigationState.forceDocumentType !==
      AllowedDocumentType.Passport
    ) {
      // change forceDocumentType to passport if it is not
      searchParams.set("forceDocumentType", AllowedDocumentType.Passport);
      setSearchParams(searchParams);
    }
  }, [itemLevelNavigationState, searchParams, setSearchParams]);

  if (
    itemLevelNavigationState.forceDocumentType !== AllowedDocumentType.Passport
  ) {
    return null;
  }

  const onScanPassportClick = () => {
    // Booking mismatch is possible with both manual and automated verification,
    // but only in case of manual one we show the form and scroll to it automatically.
    // To avoid this behavior with upload we reset the flag here.
    setHasManualBookingMismatch(false);

    setProcessingStatus(DocProcessingStatus.None, null);
    setProcessingMayTakeLong(null);

    navigate("../capture");
  };

  const onEnterManuallyClick = () => {
    showForm(true);
  };

  return (
    <StyledPassportPage>
      <div>
        <AutomatedTools>
          <MicroHeader>{textPassport("automated-tools")}</MicroHeader>
          <ToolsButtons>
            <PrimaryButton onClick={onScanPassportClick}>
              {textPassport("scan-button-title")}
            </PrimaryButton>
            <SelectFileButton
              onChange={onFileSelected}
              label={textPassport("upload-button-title")}
            />
          </ToolsButtons>
        </AutomatedTools>

        {paxSession?.consented === true && isManualInputAllowed ? (
          <ManualPassportInput
            ref={passportFormCardRef}
            isFormShown={isFormShown}
            onEnterManuallyClick={onEnterManuallyClick}
          />
        ) : null}
      </div>
      <PassportPreview />
    </StyledPassportPage>
  );
};

export default PassportPage;
